import { useState, useRef, useCallback } from 'react';
import { theme } from '../../lib/theme';
import { supabase } from '../../lib/supabase';
import { Plus, Copy, Check, ChevronDown, ChevronUp, Trash2, Edit3, Eye, X, ClipboardList, Upload, Star, GripVertical, Image as ImageIcon, Download, Sparkles } from 'lucide-react';
import EmptyState from '../common/EmptyState';
import LoadingSpinner from '../common/LoadingSpinner';
import StudioView from '../studio/StudioView';

// ============================================================
// PLATFORM FIELD MAPPINGS
// Each platform defines which fields it needs and how to label them
// ============================================================

const PLATFORMS = {
  bright_mls: {
    name: 'Bright MLS',
    color: '#2563EB',
    types: ['residential', 'commercial'],
    sections: (listing) => {
      const base = [
        { label: 'Property Address', key: 'address' },
        { label: 'City', key: 'city' },
        { label: 'State', key: 'state' },
        { label: 'Zip Code', key: 'zip' },
        { label: 'County', key: 'county' },
        { label: 'List Price', key: 'price', format: 'currency' },
        { label: 'Property Type', key: 'propertyType' },
        { label: 'Status', key: 'listingStatus' },
        { label: 'Year Built', key: 'yearBuilt' },
        { label: 'Lot Size', key: 'lotSize' },
        { label: 'Total SqFt', key: 'sqft' },
        { label: 'Parking', key: 'parking' },
        { label: 'Taxes (Annual)', key: 'taxes', format: 'currency' },
        { label: 'HOA/Condo Fee', key: 'hoaFee', format: 'currency' },
        { label: 'Public Remarks', key: 'description' },
        { label: 'Listing Agent', key: 'agentName' },
        { label: 'Listing Office', key: 'officeName' },
      ];
      if (listing.propertyType === 'residential') {
        base.splice(11, 0,
          { label: 'Bedrooms', key: 'bedrooms' },
          { label: 'Bathrooms (Full)', key: 'bathsFull' },
          { label: 'Bathrooms (Half)', key: 'bathsHalf' },
          { label: 'Basement', key: 'basement' },
          { label: 'Heating', key: 'heating' },
          { label: 'Cooling', key: 'cooling' },
        );
      } else {
        base.splice(11, 0,
          { label: 'Zoning', key: 'zoning' },
          { label: 'Cap Rate', key: 'capRate', format: 'percent' },
          { label: 'NOI', key: 'noi', format: 'currency' },
          { label: 'Building Class', key: 'buildingClass' },
          { label: 'Tenant Info', key: 'tenantInfo' },
        );
      }
      return base;
    },
  },
  loopnet: {
    name: 'LoopNet',
    color: '#059669',
    types: ['commercial'],
    sections: () => [
      { label: 'Property Name', key: 'propertyName' },
      { label: 'Property Address', key: 'address' },
      { label: 'City, State, Zip', key: '_cityStateZip' },
      { label: 'Property Type', key: 'commercialType' },
      { label: 'Property Sub-Type', key: 'commercialSubType' },
      { label: 'Sale Price', key: 'price', format: 'currency' },
      { label: 'Price / SF', key: '_pricePerSqft', format: 'currency' },
      { label: 'Building Size (SF)', key: 'sqft' },
      { label: 'Lot Size', key: 'lotSize' },
      { label: 'Year Built', key: 'yearBuilt' },
      { label: 'Zoning', key: 'zoning' },
      { label: 'Cap Rate', key: 'capRate', format: 'percent' },
      { label: 'NOI', key: 'noi', format: 'currency' },
      { label: 'Building Class', key: 'buildingClass' },
      { label: 'Number of Units', key: 'units' },
      { label: 'Occupancy', key: 'occupancy', format: 'percent' },
      { label: 'Tenant Information', key: 'tenantInfo' },
      { label: 'Lease Type', key: 'leaseType' },
      { label: 'Highlights', key: 'highlights' },
      { label: 'Description', key: 'description' },
      { label: 'Broker Name', key: 'agentName' },
      { label: 'Broker Company', key: 'officeName' },
      { label: 'Contact Phone', key: 'agentPhone' },
      { label: 'Contact Email', key: 'agentEmail' },
    ],
  },
  crexi: {
    name: 'Crexi',
    color: '#7C3AED',
    types: ['commercial'],
    sections: () => [
      { label: 'Property Name', key: 'propertyName' },
      { label: 'Address', key: 'address' },
      { label: 'City', key: 'city' },
      { label: 'State', key: 'state' },
      { label: 'Zip', key: 'zip' },
      { label: 'Asset Type', key: 'commercialType' },
      { label: 'Sub-Type', key: 'commercialSubType' },
      { label: 'Asking Price', key: 'price', format: 'currency' },
      { label: 'Building SF', key: 'sqft' },
      { label: 'Land Size', key: 'lotSize' },
      { label: 'Year Built', key: 'yearBuilt' },
      { label: 'Cap Rate', key: 'capRate', format: 'percent' },
      { label: 'NOI', key: 'noi', format: 'currency' },
      { label: 'Occupancy Rate', key: 'occupancy', format: 'percent' },
      { label: 'Number of Units', key: 'units' },
      { label: 'Zoning', key: 'zoning' },
      { label: 'Lease Type', key: 'leaseType' },
      { label: 'Investment Highlights', key: 'highlights' },
      { label: 'Property Description', key: 'description' },
      { label: 'Listing Broker', key: 'agentName' },
      { label: 'Brokerage', key: 'officeName' },
      { label: 'Phone', key: 'agentPhone' },
      { label: 'Email', key: 'agentEmail' },
    ],
  },
  wix: {
    name: 'Wix (SabalRE.com)',
    color: '#E8913A',
    types: ['residential', 'commercial'],
    sections: (listing) => {
      const base = [
        { label: 'Title', key: '_webTitle' },
        { label: 'Address', key: 'address' },
        { label: 'City', key: 'city' },
        { label: 'State', key: 'state' },
        { label: 'Zip', key: 'zip' },
        { label: 'Price', key: 'price', format: 'currency' },
        { label: 'Property Type', key: 'propertyType' },
        { label: 'Square Footage', key: 'sqft' },
        { label: 'Lot Size', key: 'lotSize' },
        { label: 'Year Built', key: 'yearBuilt' },
        { label: 'Description', key: 'description' },
        { label: 'Status', key: 'listingStatus' },
      ];
      if (listing.propertyType === 'residential') {
        base.splice(8, 0,
          { label: 'Bedrooms', key: 'bedrooms' },
          { label: 'Bathrooms', key: '_totalBaths' },
        );
      } else {
        base.splice(8, 0,
          { label: 'Cap Rate', key: 'capRate', format: 'percent' },
          { label: 'NOI', key: 'noi', format: 'currency' },
          { label: 'Zoning', key: 'zoning' },
        );
      }
      return base;
    },
  },
};

// ============================================================
// EMPTY LISTING TEMPLATE
// ============================================================

const EMPTY_LISTING = {
  id: null,
  propertyType: 'residential', // residential | commercial
  listingStatus: 'Active',
  // Address
  address: '', city: '', state: 'NJ', zip: '', county: '',
  // Basic
  price: '', sqft: '', lotSize: '', yearBuilt: '', parking: '',
  // Residential
  bedrooms: '', bathsFull: '', bathsHalf: '', basement: '', heating: '', cooling: '',
  // Commercial
  propertyName: '', commercialType: '', commercialSubType: '',
  capRate: '', noi: '', buildingClass: '', units: '', occupancy: '',
  tenantInfo: '', leaseType: '', zoning: '', highlights: '',
  // Taxes / HOA
  taxes: '', hoaFee: '',
  // Description
  description: '',
  // Agent
  agentName: 'Jenifer Raffa', officeName: 'Sabal Real Estate NJ',
  agentPhone: '', agentEmail: '',
  // Photos
  photos: [],
  // Timestamps
  createdAt: null,
};

const STATUS_OPTIONS = ['Active', 'Coming Soon', 'Under Contract', 'Pending', 'Sold', 'Withdrawn'];
const COMMERCIAL_TYPES = ['Office', 'Retail', 'Industrial', 'Multifamily', 'Mixed-Use', 'Land', 'Hospitality', 'Healthcare', 'Self-Storage', 'Other'];

// ============================================================
// DEMO LISTINGS
// ============================================================

const DEMO_LISTINGS = [
  {
    id: 1,
    propertyType: 'residential',
    listingStatus: 'Active',
    address: '142 Maple Avenue', city: 'Ridgewood', state: 'NJ', zip: '07450', county: 'Bergen',
    price: '625000', sqft: '2400', lotSize: '0.35 acres', yearBuilt: '1998', parking: '2 Car Garage',
    bedrooms: '4', bathsFull: '2', bathsHalf: '1', basement: 'Finished', heating: 'Forced Air Gas', cooling: 'Central A/C',
    description: 'Charming 4-bedroom Colonial in the heart of Ridgewood. Updated kitchen with granite counters, hardwood floors throughout. Spacious backyard with patio. Walk to top-rated schools and downtown shops.',
    taxes: '12500', hoaFee: '',
    agentName: 'Jenifer Raffa', officeName: 'Sabal Real Estate NJ', agentPhone: '(201) 555-0100', agentEmail: 'jenifer@sabalre.com',
    commercialType: '', commercialSubType: '', capRate: '', noi: '', buildingClass: '', units: '', occupancy: '', tenantInfo: '', leaseType: '', zoning: '', highlights: '', propertyName: '',
    photos: [],
    createdAt: '2026-04-08',
  },
  {
    id: 2,
    propertyType: 'commercial',
    listingStatus: 'Active',
    address: '500 Route 17 South', city: 'Paramus', state: 'NJ', zip: '07652', county: 'Bergen',
    price: '2850000', sqft: '12000', lotSize: '0.8 acres', yearBuilt: '2005', parking: '45 spaces',
    propertyName: 'Paramus Professional Plaza', commercialType: 'Office', commercialSubType: 'Multi-Tenant Office',
    capRate: '7.2', noi: '205200', buildingClass: 'B+', units: '6', occupancy: '92',
    tenantInfo: '5 of 6 units leased, avg remaining term 3.5 years', leaseType: 'NNN', zoning: 'B-2 Commercial',
    highlights: 'High-visibility Route 17 frontage. Recently renovated lobby and common areas. Strong tenant mix with staggered lease expirations.',
    description: 'Well-maintained multi-tenant office building on high-traffic Route 17. 92% occupied with strong cash flow. NNN lease structure minimizes owner expenses. Excellent investment opportunity in Bergen County\'s premier commercial corridor.',
    taxes: '42000', hoaFee: '',
    agentName: 'Jenifer Raffa', officeName: 'Sabal Real Estate NJ', agentPhone: '(201) 555-0100', agentEmail: 'jenifer@sabalre.com',
    bedrooms: '', bathsFull: '', bathsHalf: '', basement: '', heating: '', cooling: '',
    photos: [],
    createdAt: '2026-04-05',
  },
];

// ============================================================
// HELPER: Format value for display
// ============================================================

function formatValue(value, format) {
  if (!value && value !== 0) return '';
  if (format === 'currency') {
    const num = parseFloat(String(value).replace(/[^0-9.-]/g, ''));
    if (isNaN(num)) return value;
    return '$' + num.toLocaleString('en-US', { maximumFractionDigits: 0 });
  }
  if (format === 'percent') return value + '%';
  return String(value);
}

// ============================================================
// HELPER: Compute derived fields
// ============================================================

function getFieldValue(listing, key) {
  if (key === '_cityStateZip') return `${listing.city}, ${listing.state} ${listing.zip}`;
  if (key === '_totalBaths') {
    const full = parseInt(listing.bathsFull) || 0;
    const half = parseInt(listing.bathsHalf) || 0;
    return half > 0 ? `${full} full, ${half} half` : `${full}`;
  }
  if (key === '_pricePerSqft') {
    const price = parseFloat(String(listing.price).replace(/[^0-9.-]/g, ''));
    const sqft = parseFloat(listing.sqft);
    if (!isNaN(price) && !isNaN(sqft) && sqft > 0) return Math.round(price / sqft);
    return '';
  }
  if (key === '_webTitle') {
    if (listing.propertyType === 'commercial' && listing.propertyName) return listing.propertyName;
    const beds = listing.bedrooms ? `${listing.bedrooms}BR` : '';
    const baths = listing.bathsFull ? `${listing.bathsFull}BA` : '';
    const parts = [beds, baths].filter(Boolean).join('/');
    return parts ? `${parts} ${listing.city}` : listing.address;
  }
  return listing[key] ?? '';
}

// ============================================================
// STYLES
// ============================================================

const styles = {
  card: {
    background: theme.colors.card,
    borderRadius: theme.radius.md,
    border: `1px solid ${theme.colors.border}`,
    boxShadow: theme.shadow.sm,
  },
  input: {
    width: '100%', padding: '8px 12px', border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.radius.sm, fontSize: 13, color: theme.colors.text,
    outline: 'none', boxSizing: 'border-box', background: '#fff',
  },
  label: {
    display: 'block', fontSize: 11, fontWeight: 600, color: theme.colors.textLight,
    marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.03em',
  },
  sectionTitle: {
    fontSize: 13, fontWeight: 700, color: theme.colors.text, marginBottom: 12,
    paddingBottom: 6, borderBottom: `1px solid ${theme.colors.border}`,
  },
  btn: {
    padding: '8px 16px', borderRadius: theme.radius.sm, border: 'none',
    fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'inline-flex',
    alignItems: 'center', gap: 6,
  },
};

// ============================================================
// PHOTO GALLERY — Upload, reorder, caption, set cover
// ============================================================

const STORAGE_BUCKET = 'listing-photos';

function PhotoGallery({ listingId, photos, onPhotosChange, demoMode }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragSourceIndex, setDragSourceIndex] = useState(null);
  const [editingCaption, setEditingCaption] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  // Upload handler — uses Supabase Storage in production, blob URLs in demo
  const handleUpload = useCallback(async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const newPhotos = [];

    for (const file of Array.from(files)) {
      if (!file.type.startsWith('image/')) continue;

      if (demoMode || !import.meta.env.VITE_SUPABASE_URL) {
        // Demo: use local object URLs
        const url = URL.createObjectURL(file);
        newPhotos.push({
          id: Date.now() + Math.random(),
          url,
          name: file.name,
          caption: '',
          isCover: photos.length === 0 && newPhotos.length === 0,
          size: file.size,
        });
      } else {
        // Production: upload to Supabase Storage
        const ext = file.name.split('.').pop();
        const path = `${listingId}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
        const { error } = await supabase.storage.from(STORAGE_BUCKET).upload(path, file, {
          cacheControl: '3600',
          upsert: false,
        });
        if (!error) {
          const { data: urlData } = supabase.storage.from(STORAGE_BUCKET).getPublicUrl(path);
          newPhotos.push({
            id: Date.now() + Math.random(),
            url: urlData.publicUrl,
            path,
            name: file.name,
            caption: '',
            isCover: photos.length === 0 && newPhotos.length === 0,
            size: file.size,
          });
        }
      }
    }

    onPhotosChange([...photos, ...newPhotos]);
    setUploading(false);
  }, [photos, listingId, demoMode, onPhotosChange]);

  // Drop zone handler
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = e.dataTransfer?.files;
    if (files?.length) handleUpload(files);
  }, [handleUpload]);

  // Drag reorder
  const handleDragStart = (idx) => setDragSourceIndex(idx);
  const handleDragOver = (e, idx) => { e.preventDefault(); setDragOverIndex(idx); };
  const handleDragEnd = () => {
    if (dragSourceIndex !== null && dragOverIndex !== null && dragSourceIndex !== dragOverIndex) {
      const reordered = [...photos];
      const [moved] = reordered.splice(dragSourceIndex, 1);
      reordered.splice(dragOverIndex, 0, moved);
      onPhotosChange(reordered);
    }
    setDragSourceIndex(null);
    setDragOverIndex(null);
  };

  const setCover = (idx) => {
    onPhotosChange(photos.map((p, i) => ({ ...p, isCover: i === idx })));
  };

  const deletePhoto = async (idx) => {
    const photo = photos[idx];
    // If production and has storage path, delete from Supabase
    if (photo.path && !demoMode) {
      await supabase.storage.from(STORAGE_BUCKET).remove([photo.path]);
    }
    const updated = photos.filter((_, i) => i !== idx);
    // If deleted photo was cover, make first remaining photo the cover
    if (photo.isCover && updated.length > 0) updated[0].isCover = true;
    onPhotosChange(updated);
  };

  const updateCaption = (idx, caption) => {
    onPhotosChange(photos.map((p, i) => i === idx ? { ...p, caption } : p));
  };

  const downloadPhoto = (photo) => {
    const a = document.createElement('a');
    a.href = photo.url;
    a.download = photo.name || 'photo.jpg';
    a.click();
  };

  return (
    <div style={{ ...styles.card, padding: 20, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: theme.colors.text }}>
            Photos ({photos.length})
          </h3>
          <p style={{ margin: '2px 0 0', fontSize: 12, color: theme.colors.textLight }}>
            Drag to reorder · First photo or starred photo is the cover shot
          </p>
        </div>
        <button
          onClick={() => fileInputRef.current?.click()}
          style={{ ...styles.btn, background: theme.colors.primary, color: 'white', fontSize: 12, padding: '6px 14px' }}
          disabled={uploading}
        >
          <Upload size={14} /> {uploading ? 'Uploading...' : 'Upload Photos'}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => handleUpload(e.target.files)}
        />
      </div>

      {/* Drop zone when no photos */}
      {photos.length === 0 && (
        <div
          onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = theme.colors.primary; }}
          onDragLeave={(e) => { e.currentTarget.style.borderColor = theme.colors.border; }}
          onDrop={(e) => { e.currentTarget.style.borderColor = theme.colors.border; handleDrop(e); }}
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: `2px dashed ${theme.colors.border}`, borderRadius: theme.radius.md,
            padding: 40, textAlign: 'center', cursor: 'pointer', transition: 'border-color 0.2s',
          }}
        >
          <ImageIcon size={32} color={theme.colors.textLight} style={{ marginBottom: 8 }} />
          <div style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text, marginBottom: 4 }}>
            Drop photos here or click to upload
          </div>
          <div style={{ fontSize: 12, color: theme.colors.textLight }}>
            JPG, PNG, WebP · Max 10MB each
          </div>
        </div>
      )}

      {/* Photo grid */}
      {photos.length > 0 && (
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
            gap: 10,
          }}
        >
          {photos.map((photo, idx) => (
            <div
              key={photo.id}
              draggable
              onDragStart={() => handleDragStart(idx)}
              onDragOver={(e) => handleDragOver(e, idx)}
              onDragEnd={handleDragEnd}
              style={{
                position: 'relative', borderRadius: theme.radius.sm, overflow: 'hidden',
                border: dragOverIndex === idx ? `2px solid ${theme.colors.primary}` : `1px solid ${theme.colors.border}`,
                background: '#f8f9fa', cursor: 'grab',
                opacity: dragSourceIndex === idx ? 0.5 : 1,
                transition: 'opacity 0.15s, border 0.15s',
              }}
            >
              {/* Image */}
              <div
                onClick={() => setLightboxIndex(idx)}
                style={{ cursor: 'pointer', position: 'relative' }}
              >
                <img
                  src={photo.url}
                  alt={photo.caption || photo.name}
                  style={{ width: '100%', height: 120, objectFit: 'cover', display: 'block' }}
                />
                {/* Cover badge */}
                {photo.isCover && (
                  <div style={{
                    position: 'absolute', top: 6, left: 6,
                    background: theme.colors.accent, color: 'white',
                    fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
                    textTransform: 'uppercase',
                  }}>Cover</div>
                )}
                {/* Photo number */}
                <div style={{
                  position: 'absolute', top: 6, right: 6,
                  background: 'rgba(0,0,0,0.5)', color: 'white',
                  fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 4,
                }}>#{idx + 1}</div>
              </div>

              {/* Controls */}
              <div style={{ padding: '6px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', gap: 4 }}>
                  <GripVertical size={14} color={theme.colors.textLight} style={{ cursor: 'grab' }} />
                  <button
                    onClick={() => setCover(idx)}
                    title="Set as cover"
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer', padding: 2,
                      color: photo.isCover ? theme.colors.accent : theme.colors.textLight,
                    }}
                  >
                    <Star size={14} fill={photo.isCover ? theme.colors.accent : 'none'} />
                  </button>
                  <button
                    onClick={() => downloadPhoto(photo)}
                    title="Download"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: theme.colors.textLight }}
                  >
                    <Download size={14} />
                  </button>
                </div>
                <button
                  onClick={() => deletePhoto(idx)}
                  title="Delete photo"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: theme.colors.danger }}
                >
                  <Trash2 size={13} />
                </button>
              </div>

              {/* Caption */}
              {editingCaption === idx ? (
                <div style={{ padding: '0 8px 6px' }}>
                  <input
                    autoFocus
                    value={photo.caption}
                    onChange={(e) => updateCaption(idx, e.target.value)}
                    onBlur={() => setEditingCaption(null)}
                    onKeyDown={(e) => { if (e.key === 'Enter') setEditingCaption(null); }}
                    placeholder="Add caption..."
                    style={{ ...styles.input, fontSize: 11, padding: '4px 8px' }}
                  />
                </div>
              ) : (
                <div
                  onClick={() => setEditingCaption(idx)}
                  style={{
                    padding: '0 8px 6px', fontSize: 11, cursor: 'text',
                    color: photo.caption ? theme.colors.text : theme.colors.textLight,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}
                >
                  {photo.caption || 'Click to add caption...'}
                </div>
              )}
            </div>
          ))}

          {/* Add more zone */}
          <div
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = theme.colors.primary; }}
            onDragLeave={(e) => { e.currentTarget.style.borderColor = theme.colors.border; }}
            onDrop={(e) => { e.currentTarget.style.borderColor = theme.colors.border; handleDrop(e); }}
            style={{
              border: `2px dashed ${theme.colors.border}`, borderRadius: theme.radius.sm,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              minHeight: 120, cursor: 'pointer', transition: 'border-color 0.2s',
            }}
          >
            <Plus size={20} color={theme.colors.textLight} />
            <span style={{ fontSize: 11, color: theme.colors.textLight, marginTop: 4 }}>Add More</span>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <div
          onClick={() => setLightboxIndex(null)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.85)', zIndex: 500,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightboxIndex(null); }}
            style={{
              position: 'absolute', top: 16, right: 16,
              background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 20,
              width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <X size={20} color="white" />
          </button>
          {/* Prev / Next */}
          {lightboxIndex > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); setLightboxIndex(lightboxIndex - 1); }}
              style={{
                position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)',
                background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 20,
                width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', color: 'white', fontSize: 20, fontWeight: 700,
              }}
            >‹</button>
          )}
          {lightboxIndex < photos.length - 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); setLightboxIndex(lightboxIndex + 1); }}
              style={{
                position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)',
                background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 20,
                width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', color: 'white', fontSize: 20, fontWeight: 700,
              }}
            >›</button>
          )}
          <img
            src={photos[lightboxIndex]?.url}
            alt={photos[lightboxIndex]?.caption || ''}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: 8, cursor: 'default' }}
          />
          {photos[lightboxIndex]?.caption && (
            <div style={{
              position: 'absolute', bottom: 24,
              background: 'rgba(0,0,0,0.6)', color: 'white',
              padding: '8px 16px', borderRadius: 8, fontSize: 14,
            }}>{photos[lightboxIndex].caption}</div>
          )}
          <div style={{
            position: 'absolute', bottom: 60,
            color: 'rgba(255,255,255,0.6)', fontSize: 13,
          }}>{lightboxIndex + 1} / {photos.length}</div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// PLATFORM COPY PANEL
// ============================================================

function PlatformPanel({ platform, listing }) {
  const [expanded, setExpanded] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [copiedAll, setCopiedAll] = useState(false);
  const config = PLATFORMS[platform];
  const fields = config.sections(listing);

  const copyField = async (label, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(label);
      setTimeout(() => setCopiedField(null), 1500);
    } catch {}
  };

  const copyAll = async () => {
    const text = fields
      .map(f => {
        const val = formatValue(getFieldValue(listing, f.key), f.format);
        return val ? `${f.label}: ${val}` : null;
      })
      .filter(Boolean)
      .join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 2000);
    } catch {}
  };

  const filledCount = fields.filter(f => {
    const v = getFieldValue(listing, f.key);
    return v !== '' && v !== undefined && v !== null;
  }).length;

  return (
    <div style={{ ...styles.card, overflow: 'hidden' }}>
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          width: '100%', padding: '14px 16px', border: 'none', cursor: 'pointer',
          background: expanded ? `${config.color}08` : 'white',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 8, height: 8, borderRadius: 4, background: config.color,
          }} />
          <span style={{ fontWeight: 700, fontSize: 14, color: theme.colors.text }}>{config.name}</span>
          <span style={{
            fontSize: 11, color: theme.colors.textLight, background: theme.colors.bg,
            padding: '2px 8px', borderRadius: 10,
          }}>{filledCount}/{fields.length} fields</span>
        </div>
        {expanded ? <ChevronUp size={16} color={theme.colors.textLight} /> : <ChevronDown size={16} color={theme.colors.textLight} />}
      </button>

      {expanded && (
        <div style={{ padding: '0 16px 16px' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10, paddingTop: 4 }}>
            <button
              onClick={copyAll}
              style={{
                ...styles.btn,
                background: copiedAll ? theme.colors.success : config.color,
                color: 'white', fontSize: 12, padding: '6px 14px',
              }}
            >
              {copiedAll ? <Check size={14} /> : <Copy size={14} />}
              {copiedAll ? 'Copied All!' : 'Copy All Fields'}
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {fields.map((f) => {
              const rawVal = getFieldValue(listing, f.key);
              const val = formatValue(rawVal, f.format);
              const isEmpty = !rawVal && rawVal !== 0;
              return (
                <div
                  key={f.key}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '7px 10px', borderRadius: 6,
                    background: isEmpty ? '#FEF3C7' : 'transparent',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: 11, color: theme.colors.textLight, marginRight: 8 }}>{f.label}:</span>
                    <span style={{
                      fontSize: 13, color: isEmpty ? theme.colors.warning : theme.colors.text,
                      fontWeight: isEmpty ? 400 : 500,
                    }}>
                      {isEmpty ? '— not filled —' : val}
                    </span>
                  </div>
                  {!isEmpty && (
                    <button
                      onClick={() => copyField(f.label, val)}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px',
                        color: copiedField === f.label ? theme.colors.success : theme.colors.textLight,
                        display: 'flex', alignItems: 'center',
                      }}
                    >
                      {copiedField === f.label ? <Check size={14} /> : <Copy size={14} />}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// FIELD GROUP (reusable form section)
// ============================================================

function FieldGroup({ label, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={styles.sectionTitle}>{label}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
        {children}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', options, placeholder, wide }) {
  return (
    <div style={wide ? { gridColumn: '1 / -1' } : {}}>
      <label style={styles.label}>{label}</label>
      {options ? (
        <select value={value} onChange={e => onChange(e.target.value)} style={styles.input}>
          <option value="">Select...</option>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : type === 'textarea' ? (
        <textarea
          value={value} onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          rows={4}
          style={{ ...styles.input, resize: 'vertical', minHeight: 80 }}
        />
      ) : (
        <input
          type={type} value={value} onChange={e => onChange(e.target.value)}
          placeholder={placeholder} style={styles.input}
        />
      )}
    </div>
  );
}

// ============================================================
// LISTING FORM
// ============================================================

function ListingForm({ listing, onChange, onSave, onCancel }) {
  const update = (key, val) => onChange({ ...listing, [key]: val });
  const isCommercial = listing.propertyType === 'commercial';

  return (
    <div>
      {/* Property Type Toggle */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {['residential', 'commercial'].map(t => (
          <button
            key={t}
            onClick={() => update('propertyType', t)}
            style={{
              ...styles.btn,
              background: listing.propertyType === t ? theme.colors.primary : theme.colors.bg,
              color: listing.propertyType === t ? 'white' : theme.colors.textLight,
              textTransform: 'capitalize', flex: 1, justifyContent: 'center',
            }}
          >{t}</button>
        ))}
      </div>

      <FieldGroup label="Address">
        <Field label="Street Address" value={listing.address} onChange={v => update('address', v)} placeholder="123 Main St" wide />
        <Field label="City" value={listing.city} onChange={v => update('city', v)} placeholder="Ridgewood" />
        <Field label="State" value={listing.state} onChange={v => update('state', v)} placeholder="NJ" />
        <Field label="Zip Code" value={listing.zip} onChange={v => update('zip', v)} placeholder="07450" />
        <Field label="County" value={listing.county} onChange={v => update('county', v)} placeholder="Bergen" />
      </FieldGroup>

      <FieldGroup label="Listing Details">
        <Field label="List Price ($)" value={listing.price} onChange={v => update('price', v)} placeholder="625000" />
        <Field label="Status" value={listing.listingStatus} onChange={v => update('listingStatus', v)} options={STATUS_OPTIONS} />
        <Field label="Total SqFt" value={listing.sqft} onChange={v => update('sqft', v)} placeholder="2400" />
        <Field label="Lot Size" value={listing.lotSize} onChange={v => update('lotSize', v)} placeholder="0.35 acres" />
        <Field label="Year Built" value={listing.yearBuilt} onChange={v => update('yearBuilt', v)} placeholder="2000" />
        <Field label="Parking" value={listing.parking} onChange={v => update('parking', v)} placeholder="2 Car Garage" />
        <Field label="Annual Taxes ($)" value={listing.taxes} onChange={v => update('taxes', v)} placeholder="12500" />
        <Field label="HOA / Condo Fee ($)" value={listing.hoaFee} onChange={v => update('hoaFee', v)} placeholder="250" />
      </FieldGroup>

      {/* Residential-specific fields */}
      {!isCommercial && (
        <FieldGroup label="Residential Details">
          <Field label="Bedrooms" value={listing.bedrooms} onChange={v => update('bedrooms', v)} placeholder="4" />
          <Field label="Full Baths" value={listing.bathsFull} onChange={v => update('bathsFull', v)} placeholder="2" />
          <Field label="Half Baths" value={listing.bathsHalf} onChange={v => update('bathsHalf', v)} placeholder="1" />
          <Field label="Basement" value={listing.basement} onChange={v => update('basement', v)} options={['None', 'Unfinished', 'Partial', 'Finished']} />
          <Field label="Heating" value={listing.heating} onChange={v => update('heating', v)} placeholder="Forced Air Gas" />
          <Field label="Cooling" value={listing.cooling} onChange={v => update('cooling', v)} placeholder="Central A/C" />
        </FieldGroup>
      )}

      {/* Commercial-specific fields */}
      {isCommercial && (
        <FieldGroup label="Commercial Details">
          <Field label="Property Name" value={listing.propertyName} onChange={v => update('propertyName', v)} placeholder="Paramus Professional Plaza" wide />
          <Field label="Commercial Type" value={listing.commercialType} onChange={v => update('commercialType', v)} options={COMMERCIAL_TYPES} />
          <Field label="Sub-Type" value={listing.commercialSubType} onChange={v => update('commercialSubType', v)} placeholder="Multi-Tenant Office" />
          <Field label="Cap Rate (%)" value={listing.capRate} onChange={v => update('capRate', v)} placeholder="7.2" />
          <Field label="NOI ($)" value={listing.noi} onChange={v => update('noi', v)} placeholder="205200" />
          <Field label="Building Class" value={listing.buildingClass} onChange={v => update('buildingClass', v)} options={['A', 'A+', 'B', 'B+', 'C', 'C+']} />
          <Field label="Number of Units" value={listing.units} onChange={v => update('units', v)} placeholder="6" />
          <Field label="Occupancy (%)" value={listing.occupancy} onChange={v => update('occupancy', v)} placeholder="92" />
          <Field label="Zoning" value={listing.zoning} onChange={v => update('zoning', v)} placeholder="B-2 Commercial" />
          <Field label="Lease Type" value={listing.leaseType} onChange={v => update('leaseType', v)} options={['Gross', 'Modified Gross', 'NNN', 'Absolute Net', 'Percentage']} />
          <Field label="Tenant Info" value={listing.tenantInfo} onChange={v => update('tenantInfo', v)} type="textarea" placeholder="Tenant details, lease terms..." wide />
          <Field label="Investment Highlights" value={listing.highlights} onChange={v => update('highlights', v)} type="textarea" placeholder="Key selling points..." wide />
        </FieldGroup>
      )}

      <FieldGroup label="Description">
        <Field label="Public Remarks / Description" value={listing.description} onChange={v => update('description', v)} type="textarea" placeholder="Full property description for MLS and website..." wide />
      </FieldGroup>

      <FieldGroup label="Agent / Contact">
        <Field label="Listing Agent" value={listing.agentName} onChange={v => update('agentName', v)} />
        <Field label="Office Name" value={listing.officeName} onChange={v => update('officeName', v)} />
        <Field label="Phone" value={listing.agentPhone} onChange={v => update('agentPhone', v)} placeholder="(201) 555-0100" />
        <Field label="Email" value={listing.agentEmail} onChange={v => update('agentEmail', v)} placeholder="jenifer@sabalre.com" />
      </FieldGroup>

      {/* Save / Cancel */}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 12, borderTop: `1px solid ${theme.colors.border}` }}>
        <button onClick={onCancel} style={{ ...styles.btn, background: theme.colors.bg, color: theme.colors.textLight }}>Cancel</button>
        <button onClick={onSave} style={{ ...styles.btn, background: theme.colors.primary, color: 'white' }}>
          {listing.id ? 'Save Changes' : 'Create Listing'}
        </button>
      </div>
    </div>
  );
}

// ============================================================
// MAIN LISTING HUB VIEW
// ============================================================

// ============================================================
// TOP-LEVEL TABS: My Listings + AI Studio
// ============================================================

const HUB_TABS = [
  { key: 'listings', label: 'My Listings', icon: ClipboardList },
  { key: 'studio', label: 'AI Studio', icon: Sparkles },
];

export default function ListingHub({ showToast, demoMode }) {
  const [hubTab, setHubTab] = useState('listings');

  return (
    <div>
      {/* Top tab strip */}
      <div style={{
        display: 'flex', gap: 0, borderBottom: `1px solid ${theme.colors.border}`,
        padding: '0 28px', background: 'white',
      }}>
        {HUB_TABS.map(tab => {
          const Icon = tab.icon;
          const active = hubTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setHubTab(tab.key)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '12px 20px', fontSize: 14, fontWeight: active ? 600 : 500,
                color: active ? theme.colors.primary : theme.colors.textLight,
                background: 'none', border: 'none', cursor: 'pointer',
                borderBottom: active ? `2px solid ${theme.colors.primary}` : '2px solid transparent',
                marginBottom: -1, transition: 'all 0.15s ease',
              }}
            >
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {hubTab === 'studio' ? (
        <StudioView demoMode={demoMode} showToast={showToast} />
      ) : (
        <ListingsContent showToast={showToast} demoMode={demoMode} />
      )}
    </div>
  );
}

// ============================================================
// LISTINGS CONTENT (original ListingHub internals)
// ============================================================

function ListingsContent({ showToast, demoMode }) {
  const [listings, setListings] = useState(demoMode ? DEMO_LISTINGS : []);
  const [view, setView] = useState('list'); // list | form | detail
  const [editListing, setEditListing] = useState(null);
  const [selectedListing, setSelectedListing] = useState(null);

  const handleNew = () => {
    setEditListing({ ...EMPTY_LISTING, id: Date.now(), createdAt: new Date().toISOString().split('T')[0] });
    setView('form');
  };

  const handleEdit = (listing) => {
    setEditListing({ ...listing });
    setView('form');
  };

  const handleSave = () => {
    setListings(prev => {
      const exists = prev.find(l => l.id === editListing.id);
      if (exists) return prev.map(l => l.id === editListing.id ? editListing : l);
      return [editListing, ...prev];
    });
    showToast?.('Listing saved!');
    setSelectedListing(editListing);
    setView('detail');
  };

  const handleDelete = (id) => {
    setListings(prev => prev.filter(l => l.id !== id));
    showToast?.('Listing deleted');
    setView('list');
    setSelectedListing(null);
  };

  const handlePhotosChange = (listingId, photos) => {
    setListings(prev => prev.map(l => l.id === listingId ? { ...l, photos } : l));
    if (selectedListing?.id === listingId) {
      setSelectedListing(prev => ({ ...prev, photos }));
    }
  };

  // ---- LIST VIEW ----
  if (view === 'list') {
    return (
      <div style={{ padding: '24px 28px', maxWidth: 1100, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: theme.colors.text }}>Listing Hub</h2>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: theme.colors.textLight }}>
              Enter listing details once — copy to Bright MLS, LoopNet, Crexi, and Wix
            </p>
          </div>
          <button onClick={handleNew} style={{ ...styles.btn, background: theme.colors.primary, color: 'white' }}>
            <Plus size={16} /> New Listing
          </button>
        </div>

        {/* Listings table */}
        {listings.length === 0 ? (
          <div data-tour="listing-cards">
            <EmptyState
              type="listings"
              onAction={handleNew}
            />
          </div>
        ) : (
          <div data-tour="listing-cards" style={{ ...styles.card, overflow: 'hidden' }}>
            {listings.map((listing, i) => (
              <div
                key={listing.id}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '14px 18px',
                  borderBottom: i < listings.length - 1 ? `1px solid ${theme.colors.border}` : 'none',
                  cursor: 'pointer',
                  transition: 'background 0.1s',
                }}
                onClick={() => { setSelectedListing(listing); setView('detail'); }}
                onMouseEnter={e => e.currentTarget.style.background = theme.colors.bg}
                onMouseLeave={e => e.currentTarget.style.background = 'white'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 0 }}>
                  <div style={{
                    width: 10, height: 10, borderRadius: 5,
                    background: listing.propertyType === 'commercial' ? theme.colors.primary : theme.colors.accent,
                    flexShrink: 0,
                  }} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: theme.colors.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {listing.propertyName || listing.address || 'Untitled'}
                    </div>
                    <div style={{ fontSize: 12, color: theme.colors.textLight }}>
                      {listing.city}{listing.state ? `, ${listing.state}` : ''} {listing.zip} · {listing.propertyType === 'commercial' ? listing.commercialType || 'Commercial' : 'Residential'}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0 }}>
                  <span style={{ fontWeight: 700, fontSize: 14, color: theme.colors.text }}>
                    {listing.price ? formatValue(listing.price, 'currency') : '—'}
                  </span>
                  <span style={{
                    fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 10,
                    background: listing.listingStatus === 'Active' ? '#D1FAE5' : listing.listingStatus === 'Sold' ? '#DBEAFE' : '#FEF3C7',
                    color: listing.listingStatus === 'Active' ? '#065F46' : listing.listingStatus === 'Sold' ? '#1E40AF' : '#92400E',
                  }}>{listing.listingStatus}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ---- FORM VIEW ----
  if (view === 'form' && editListing) {
    return (
      <div style={{ padding: '24px 28px', maxWidth: 900, margin: '0 auto' }}>
        <div style={{ marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: theme.colors.text }}>
            {editListing.id && listings.find(l => l.id === editListing.id) ? 'Edit Listing' : 'New Listing'}
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: theme.colors.textLight }}>
            Fill in all details — platform copy panels will use these fields
          </p>
        </div>
        <div style={{ ...styles.card, padding: 24 }}>
          <ListingForm
            listing={editListing}
            onChange={setEditListing}
            onSave={handleSave}
            onCancel={() => {
              if (selectedListing) setView('detail');
              else setView('list');
            }}
          />
        </div>
      </div>
    );
  }

  // ---- DETAIL VIEW (with platform copy panels) ----
  if (view === 'detail' && selectedListing) {
    const listing = listings.find(l => l.id === selectedListing.id) || selectedListing;
    const isCommercial = listing.propertyType === 'commercial';
    const applicablePlatforms = Object.keys(PLATFORMS).filter(
      p => PLATFORMS[p].types.includes(listing.propertyType)
    );

    return (
      <div style={{ padding: '24px 28px', maxWidth: 1100, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <button
              onClick={() => { setView('list'); setSelectedListing(null); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.textLight, fontSize: 12, marginBottom: 6, padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}
            >← Back to Listings</button>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: theme.colors.text }}>
              {listing.propertyName || listing.address || 'Untitled Listing'}
            </h2>
            <div style={{ display: 'flex', gap: 10, marginTop: 6, alignItems: 'center' }}>
              <span style={{
                fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 10,
                background: isCommercial ? '#EDE9FE' : '#FFF7ED',
                color: isCommercial ? '#5B21B6' : '#C2410C',
              }}>{isCommercial ? 'Commercial' : 'Residential'}</span>
              <span style={{
                fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 10,
                background: listing.listingStatus === 'Active' ? '#D1FAE5' : '#FEF3C7',
                color: listing.listingStatus === 'Active' ? '#065F46' : '#92400E',
              }}>{listing.listingStatus}</span>
              <span style={{ fontSize: 13, color: theme.colors.textLight }}>
                {listing.city}, {listing.state} {listing.zip}
              </span>
              {listing.price && (
                <span style={{ fontSize: 15, fontWeight: 700, color: theme.colors.text }}>
                  {formatValue(listing.price, 'currency')}
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => handleEdit(listing)} style={{ ...styles.btn, background: theme.colors.bg, color: theme.colors.text }}>
              <Edit3 size={14} /> Edit
            </button>
            <button onClick={() => handleDelete(listing.id)} style={{ ...styles.btn, background: '#FEE2E2', color: theme.colors.danger }}>
              <Trash2 size={14} /> Delete
            </button>
          </div>
        </div>

        {/* Photo Gallery */}
        <PhotoGallery
          listingId={listing.id}
          photos={listing.photos || []}
          onPhotosChange={(photos) => handlePhotosChange(listing.id, photos)}
          demoMode={demoMode}
        />

        {/* Platform Copy Panels */}
        <div style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: theme.colors.text, marginBottom: 4 }}>
            Copy to Platforms
          </h3>
          <p style={{ fontSize: 12, color: theme.colors.textLight, margin: '0 0 12px' }}>
            Expand a platform panel below, then copy individual fields or all at once.
            {isCommercial ? '' : ' LoopNet and Crexi are commercial-only and not shown for residential listings.'}
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {applicablePlatforms.map(p => (
            <PlatformPanel key={p} platform={p} listing={listing} />
          ))}
        </div>
      </div>
    );
  }

  // fallback
  return null;
}
